'use client';

import { useState, useEffect, useRef, useCallback, startTransition } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Menu, X, BookOpen, Code, Globe, Sun, Moon, ChevronDown, Users, PenTool,
    Presentation, Box, Image as ImageIcon, Palette, Compass, Landmark, ClipboardList,
    DollarSign, CreditCard, Accessibility, Bot, Database, ExternalLink,
    Layout, Home, Briefcase, Layers, Radio, CalendarDays, BookMarked,
    Lightbulb, Play, Sparkles, GalleryHorizontalEnd, ArrowRightLeft,
    Link2, Globe2, Building2, BadgeDollarSign, Armchair, FileSpreadsheet,
    TrendingUp, Award, Brain, ShieldAlert,
    type LucideIcon,
} from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { openConsultationBot } from './ConsultationBot';
import { openPopupRAGChat } from './PopupRAGChat';
import { useViewMode } from './ViewModeContext';
import { useTheme } from './ThemeContext';
import { useI18n, LOCALE_LABELS, Locale } from '@/i18n/I18nProvider';

// Pages that use AppNavbar or have their own inline nav
const APP_NAVBAR_ROUTES = [
    '/flow', '/cad-agent', '/ppt-agent', '/skp-agent', '/ren-agent',
    '/color-agent', '/design-agent', '/btl-agent', '/boq-agent',
    '/cost-agent', '/pay-agent', '/bf-agent', '/furniture-agent',
    '/boq-automation', '/grid', '/chat',
    '/dashboard', '/agent', '/live', '/cowork',
    '/admin', '/ir', '/case', '/ontology', '/snapcodex',
    '/ptk', '/scheduler', '/chronology', '/dslm', '/ai-hub',
    '/analytics', '/approval', '/demo', '/guide',
    '/agent-dashboard', '/agent-team', '/billing', '/usage', '/pricing',
];

type DropdownId = 'company' | 'platform' | 'aitools' | 'family' | null;

function DropdownButton({ label, icon: Icon, children, badge, isDark, isOpen, onEnter, onLeave, onToggle }: {
    label: string;
    icon: LucideIcon;
    children: React.ReactNode;
    badge?: string;
    isDark: boolean;
    isOpen: boolean;
    onEnter: () => void;
    onLeave: () => void;
    onToggle: () => void;
}) {
    return (
        <div className="relative" onMouseEnter={onEnter} onMouseLeave={onLeave}>
            <button
                onClick={onToggle}
                className={`whitespace-nowrap flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-all duration-200 cursor-pointer ${
                    isOpen
                        ? isDark ? 'bg-white/10 text-white font-semibold' : 'bg-gray-100 text-gray-900 font-semibold'
                        : isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-black/5'
                }`}
            >
                <Icon className="w-3.5 h-3.5" />
                {label}
                {badge ? <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400 font-mono">{badge}</span> : null}
                <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
                {isOpen ? (
                    <motion.div
                        initial={{ opacity: 0, y: -6, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.97 }}
                        transition={{ duration: 0.12 }}
                        className={`absolute left-0 top-full mt-1 rounded-2xl border shadow-2xl overflow-hidden z-[100] ${isDark ? 'bg-gray-950/95 backdrop-blur-xl border-white/15' : 'bg-white border-gray-200 shadow-xl'}`}
                    >
                        {children}
                    </motion.div>
                ) : null}
            </AnimatePresence>
        </div>
    );
}

export function Navbar() {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState<DropdownId>(null);
    const [mobileExpanded, setMobileExpanded] = useState<DropdownId>(null);
    const pathname = usePathname();
    const { mode, toggle } = useViewMode();
    const { theme, toggleTheme } = useTheme();
    const { locale, setLocale, t } = useI18n();
    const isDark = theme === 'dark';
    const langRef = useRef<HTMLDivElement>(null);
    const navRef = useRef<HTMLDivElement>(null);
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => { startTransition(() => { setIsOpen(false); setActiveDropdown(null); }); }, [pathname]);

    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
            if (navRef.current && !navRef.current.contains(e.target as Node)) setActiveDropdown(null);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const hasOwnNavbar = APP_NAVBAR_ROUTES.some(route => pathname.startsWith(route));
    if (hasOwnNavbar) return null;

    const tt = (key: string, fallback: string) => {
        const val = t(key);
        return (val && val !== key) ? val : fallback;
    };

    // ── Smooth dropdown hover handling ──
    const handleDropdownEnter = (id: DropdownId) => {
        if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
        setActiveDropdown(id);
    };
    const handleDropdownLeave = () => {
        closeTimerRef.current = setTimeout(() => setActiveDropdown(null), 150);
    };

    // ── Menu Groups ──
    const COMPANY_LINKS = [
        { href: '/portfolio/hotel-interior', label: tt('nav.portfolio', '호텔인테리어'), icon: <GalleryHorizontalEnd className="w-3.5 h-3.5" />, color: 'from-rose-500 to-pink-600', desc: '호텔인테리어 포트폴리오', isHash: false },
        { href: '#insight', label: 'Insight', icon: <Lightbulb className="w-3.5 h-3.5" />, color: 'from-amber-400 to-orange-500', desc: '인사이트 · 비전', isHash: true },
        { href: '#design-process', label: tt('nav.designProcess', '디자인프로세스'), icon: <ArrowRightLeft className="w-3.5 h-3.5" />, color: 'from-sky-500 to-blue-600', desc: '설계 워크플로우', isHash: true },
        { href: '#youtube', label: 'Youtube', icon: <Play className="w-3.5 h-3.5" />, color: 'from-red-500 to-rose-600', desc: '영상 콘텐츠', isHash: true },
        { href: '#agent-collab', label: tt('nav.aiSolution', 'AI솔루션'), icon: <Sparkles className="w-3.5 h-3.5" />, color: 'from-violet-500 to-purple-600', desc: 'AI 기반 자동화', isHash: true },
        { href: 'dslm', label: 'DSLM', icon: <Brain className="w-3.5 h-3.5" />, color: 'from-teal-500 to-emerald-600', desc: '도메인 특화 모델', isHash: false },
    ];

    const PLATFORM_LINKS = [
        { href: '/snapcodex', label: 'SnapCodex', icon: <Layers className="w-3.5 h-3.5" />, desc: 'AI 건축설계 플랫폼', color: 'from-blue-500 to-indigo-600' },
        { href: '/agent', label: 'SnapAgent', icon: <Bot className="w-3.5 h-3.5" />, desc: 'AI 에이전트 시스템', color: 'from-violet-500 to-purple-600' },
        { href: '/live', label: 'LIVE', icon: <Radio className="w-3.5 h-3.5" />, desc: '실시간 그리드 모니터', color: 'from-red-500 to-rose-600', isLive: true },
        { href: '/ir', label: 'IR', icon: <TrendingUp className="w-3.5 h-3.5" />, desc: 'Investor Relations', color: 'from-indigo-500 to-blue-600' },
        { href: '/case/ptk', label: '실증 사례', icon: <Award className="w-3.5 h-3.5" />, desc: 'A프로젝트 데이터 투명성', color: 'from-emerald-500 to-teal-600', isLive: true },
        { href: '/pricing', label: 'Pricing', icon: <BadgeDollarSign className="w-3.5 h-3.5" />, desc: '상용 서비스 요금제', color: 'from-emerald-500 to-green-600' },
        { href: '/ptk', label: 'A프로젝트', icon: <Building2 className="w-3.5 h-3.5" />, desc: '실증 프로젝트', color: 'from-amber-500 to-orange-600', isLive: true },
        { href: '/scheduler', label: 'Scheduler', icon: <CalendarDays className="w-3.5 h-3.5" />, desc: '공정 스케줄 관리', color: 'from-cyan-500 to-teal-600' },
        { href: '/guide', label: 'Guide', icon: <BookMarked className="w-3.5 h-3.5" />, desc: '시스템 매뉴얼', color: 'from-gray-500 to-slate-600' },
        { href: '/flow', label: 'Flow', icon: <Users className="w-3.5 h-3.5" />, desc: '협업 · 프로젝트 관리', color: 'from-sky-500 to-blue-600' },
        { href: '/audit-dashboard', label: 'Audit Dashboard', icon: <ShieldAlert className="w-3.5 h-3.5" />, desc: '에이전트 감시 및 모니터링', color: 'from-fuchsia-500 to-pink-600', isLive: true },
    ];

    const AI_TOOLS_LIST = [
        { href: '/cad-agent', label: 'CAD', icon: <PenTool className="w-3.5 h-3.5" />, color: 'from-emerald-500 to-cyan-600', desc: 'DWG 분석 · 변환' },
        { href: '/ppt-agent', label: 'PPT', icon: <Presentation className="w-3.5 h-3.5" />, color: 'from-blue-500 to-indigo-600', desc: 'PPT 자동 생성' },
        { href: '/skp-agent', label: 'SKP', icon: <Box className="w-3.5 h-3.5" />, color: 'from-sky-500 to-blue-600', desc: 'SketchUp 3D' },
        { href: '/ren-agent', label: 'REN', icon: <ImageIcon className="w-3.5 h-3.5" />, color: 'from-purple-500 to-pink-600', desc: 'AI 렌더링' },
        { href: '/color-agent', label: 'COLOR', icon: <Palette className="w-3.5 h-3.5" />, color: 'from-pink-500 to-rose-600', desc: '색채 분석' },
        { href: '/design-agent', label: 'DESIGN', icon: <Compass className="w-3.5 h-3.5" />, color: 'from-teal-500 to-emerald-600', desc: '설계 기획' },
        { href: '/btl-agent', label: 'BTL', icon: <Landmark className="w-3.5 h-3.5" />, color: 'from-amber-500 to-orange-600', desc: 'BTL 자동화' },
        { href: '/boq-agent', label: 'BOQ', icon: <ClipboardList className="w-3.5 h-3.5" />, color: 'from-cyan-500 to-blue-600', desc: '수량 산출' },
        { href: '/cost-agent', label: 'COST', icon: <DollarSign className="w-3.5 h-3.5" />, color: 'from-emerald-500 to-green-600', desc: '공사비 예측' },
        { href: '/pay-agent', label: 'PAY', icon: <CreditCard className="w-3.5 h-3.5" />, color: 'from-violet-500 to-purple-600', desc: '기성 관리' },
        { href: '/bf-agent', label: 'BF', icon: <Accessibility className="w-3.5 h-3.5" />, color: 'from-sky-500 to-cyan-600', desc: 'BF 인증' },
        { href: '/furniture-agent', label: 'FURNITURE', icon: <Armchair className="w-3.5 h-3.5" />, color: 'from-orange-500 to-red-600', desc: '제작가구 견적' },
        { href: '/boq-automation', label: 'BOQ Auto', icon: <FileSpreadsheet className="w-3.5 h-3.5" />, color: 'from-lime-500 to-green-600', desc: '내역서 자동화' },
    ];

    const FAMILY_SITES = [
        { href: 'https://cost.snapcodex.com', label: 'SnapCOST', icon: <ClipboardList className="w-3.5 h-3.5" />, desc: 'AI 적산·원가 플랫폼 · 상용 서비스', color: 'from-blue-500 to-cyan-500' },
        { href: 'https://gov.snap-codex.com', label: 'SnapGOV', icon: <Building2 className="w-3.5 h-3.5" />, desc: '정부 설계 AI 정책허브', color: 'from-blue-600 to-indigo-700' },
        { href: 'https://price.snap-codex.com', label: 'SnapPrice', icon: <DollarSign className="w-3.5 h-3.5" />, desc: '공사비 AI 분석·예측', color: 'from-emerald-500 to-teal-600' },
        { href: 'https://nft.snap-codex.com', label: 'SnapNFT', icon: <Link2 className="w-3.5 h-3.5" />, desc: '블록체인 자산 인증', color: 'from-purple-500 to-pink-600' },
        { href: 'https://snap-codex.com', label: 'SnapCodex Hub', icon: <Globe2 className="w-3.5 h-3.5" />, desc: '패밀리 허브 포탈', color: 'from-gray-500 to-slate-700' },
    ];

    return (
        <>
            <nav
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
                        ? isDark
                            ? 'bg-gray-950/90 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-black/20'
                            : 'bg-white/92 backdrop-blur-xl border-b border-black/5 shadow-sm'
                        : isDark
                            ? 'bg-gray-950/60 backdrop-blur-md'
                            : 'bg-white/80 backdrop-blur-md'
                    }`}
            >
                <div className="w-full px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between" ref={navRef}>
                    {/* Logo */}
                    <Link href="/" className="flex items-center gap-2 group shrink-0">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs sm:text-sm group-hover:scale-110 transition-transform">
                            D
                        </div>
                        <span className={`text-base sm:text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Desinsight
                        </span>
                    </Link>

                    {/* Desktop Nav — Grouped Dropdowns */}
                    <div className="hidden lg:flex items-center gap-1">
                        {/* Home */}
                        <Link
                            href="/"
                            className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-all ${
                                pathname === '/'
                                    ? isDark ? 'bg-white/10 text-white font-semibold' : 'bg-blue-50 text-blue-700 font-semibold'
                                    : isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-black/5'
                            }`}
                        >
                            <Home className="w-3.5 h-3.5" />
                            홈
                        </Link>

                        {/* Company Dropdown */}
                        <DropdownButton label="회사소개" icon={Briefcase} isDark={isDark} isOpen={activeDropdown === 'company'} onEnter={() => handleDropdownEnter('company')} onLeave={handleDropdownLeave} onToggle={() => setActiveDropdown(activeDropdown === 'company' ? null : 'company')}>
                            <div className="w-64 p-1.5 space-y-0.5">
                                {COMPANY_LINKS.map(link => (
                                    <a
                                        key={link.href}
                                        href={pathname === '/' ? link.href : `/${link.href}`}
                                        onClick={() => { setActiveDropdown(null); setIsOpen(false); }}
                                        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${isDark ? 'text-gray-300 hover:bg-white/[0.06]' : 'text-gray-700 hover:bg-gray-50'}`}
                                    >
                                        <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${link.color} flex items-center justify-center text-white shrink-0`}>
                                            {link.icon}
                                        </div>
                                        <div>
                                            <div className={`text-xs font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{link.label}</div>
                                            <div className={`text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{link.desc}</div>
                                        </div>
                                    </a>
                                ))}
                            </div>
                        </DropdownButton>

                        {/* Platform Dropdown */}
                        <DropdownButton label="플랫폼" icon={Layers} isDark={isDark} isOpen={activeDropdown === 'platform'} onEnter={() => handleDropdownEnter('platform')} onLeave={handleDropdownLeave} onToggle={() => setActiveDropdown(activeDropdown === 'platform' ? null : 'platform')}>
                            <div className="w-64 p-1.5 space-y-0.5">
                                {PLATFORM_LINKS.map(link => (
                                    <Link
                                        key={link.href}
                                        href={link.href}
                                        onClick={() => { setActiveDropdown(null); setIsOpen(false); }}
                                        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
                                            pathname.startsWith(link.href)
                                                ? isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900'
                                                : isDark ? 'text-gray-300 hover:bg-white/[0.06]' : 'text-gray-700 hover:bg-gray-50'
                                        }`}
                                    >
                                        <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${link.color} flex items-center justify-center text-white shrink-0`}>
                                            {link.icon}
                                        </div>
                                        <div className="flex-1">
                                            <div className="text-xs font-bold flex items-center gap-1.5">
                                                {link.label}
                                                {link.isLive && <span className="text-red-500 text-[8px] animate-pulse">● LIVE</span>}
                                            </div>
                                            <div className={`text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{link.desc}</div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </DropdownButton>

                        {/* AI Tools Dropdown */}
                        <DropdownButton label="AI Tools" icon={Bot} badge={`${AI_TOOLS_LIST.length}`} isDark={isDark} isOpen={activeDropdown === 'aitools'} onEnter={() => handleDropdownEnter('aitools')} onLeave={handleDropdownLeave} onToggle={() => setActiveDropdown(activeDropdown === 'aitools' ? null : 'aitools')}>
                            <div className="w-72">
                                <div className={`px-3 py-2 border-b text-[10px] font-bold ${isDark ? 'border-white/10 text-gray-500 bg-white/[0.02]' : 'border-gray-100 text-gray-400 bg-gray-50'}`}>
                                    SnapGRID AI Agent Tools — {AI_TOOLS_LIST.length}개
                                </div>
                                <div className="max-h-[380px] overflow-y-auto p-1.5 space-y-0.5">
                                    {AI_TOOLS_LIST.map(tool => (
                                        <Link key={tool.href} href={tool.href}
                                            onClick={() => { setActiveDropdown(null); setIsOpen(false); }}
                                            className={`flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-all ${
                                                pathname.startsWith(tool.href)
                                                    ? isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-900'
                                                    : isDark ? 'text-gray-300 hover:bg-white/[0.04]' : 'text-gray-700 hover:bg-gray-50'
                                            }`}>
                                            <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${tool.color} flex items-center justify-center text-white shrink-0`}>
                                                {tool.icon}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[11px] font-bold">{tool.label}</div>
                                                <div className={`text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{tool.desc}</div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </DropdownButton>

                        {/* Family Sites Dropdown */}
                        <DropdownButton label="Family" icon={Layout} isDark={isDark} isOpen={activeDropdown === 'family'} onEnter={() => handleDropdownEnter('family')} onLeave={handleDropdownLeave} onToggle={() => setActiveDropdown(activeDropdown === 'family' ? null : 'family')}>
                            <div className="w-64">
                                <div className={`px-3 py-2 border-b text-[10px] font-bold ${isDark ? 'border-white/10 text-gray-500 bg-white/[0.02]' : 'border-gray-100 text-gray-400 bg-gray-50'}`}>
                                    SnapCodex Family — 패밀리 사이트
                                </div>
                                <div className="p-1.5 space-y-0.5">
                                    {FAMILY_SITES.map(site => (
                                        <a key={site.href} href={site.href} target="_blank" rel="noopener noreferrer"
                                            onClick={() => { setActiveDropdown(null); setIsOpen(false); }}
                                            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all group ${isDark ? 'text-gray-300 hover:bg-white/[0.04]' : 'text-gray-700 hover:bg-gray-50'}`}>
                                            <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${site.color} flex items-center justify-center text-white shrink-0`}>
                                                {site.icon}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[11px] font-bold flex items-center gap-1">
                                                    {site.label}
                                                    <ExternalLink className="w-2.5 h-2.5 opacity-0 group-hover:opacity-60 transition-opacity" />
                                                </div>
                                                <div className={`text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{site.desc}</div>
                                            </div>
                                        </a>
                                    ))}
                                </div>
                                <div className={`px-3 py-2 border-t text-center ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                                    <a href="https://github.com/desinsight/SnapCodex-Family" target="_blank" rel="noopener noreferrer"
                                        className={`text-[10px] flex items-center justify-center gap-1 ${isDark ? 'text-gray-600 hover:text-gray-400' : 'text-gray-400 hover:text-gray-600'}`}>
                                        GitHub <ExternalLink className="w-2.5 h-2.5" />
                                    </a>
                                </div>
                            </div>
                        </DropdownButton>
                    </div>

                    {/* Desktop CTA */}
                    <div className="hidden lg:flex items-center gap-2 shrink-0">
                        {/* Theme */}
                        <button
                            onClick={toggleTheme}
                            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all cursor-pointer ${isDark
                                    ? 'bg-white/5 border border-white/10 hover:bg-white/10 text-yellow-400'
                                    : 'bg-gray-100 border border-gray-200 hover:bg-gray-200 text-blue-600'
                                }`}
                            title={isDark ? 'Light mode' : 'Dark mode'}
                        >
                            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                        </button>
                        {/* Language */}
                        <div className="relative" ref={langRef}>
                            <button
                                onClick={() => setLangOpen(!langOpen)}
                                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-all cursor-pointer ${isDark ? 'bg-white/5 border border-white/10 hover:bg-white/10' : 'bg-gray-100 border border-gray-200 hover:bg-gray-200'}`}
                            >
                                <Globe className="w-3.5 h-3.5 text-gray-400" />
                                <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>{LOCALE_LABELS[locale].flag}</span>
                            </button>
                            <AnimatePresence>
                                {langOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -5 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -5 }}
                                        className={`absolute top-full right-0 mt-2 border rounded-xl overflow-hidden shadow-xl z-50 min-w-[140px] ${isDark ? 'bg-gray-900 border-white/10' : 'bg-white border-gray-200'}`}
                                    >
                                        {(Object.keys(LOCALE_LABELS) as Locale[]).map(l => (
                                            <button
                                                key={l}
                                                onClick={() => { setLocale(l); setLangOpen(false); }}
                                                className={`w-full px-4 py-2.5 text-left text-xs flex items-center gap-2 transition-colors cursor-pointer ${locale === l ? 'bg-blue-500/10 text-blue-600' : isDark ? 'text-gray-400 hover:bg-white/5 hover:text-white' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}
                                            >
                                                <span>{LOCALE_LABELS[l].flag}</span>
                                                <span>{LOCALE_LABELS[l].name}</span>
                                            </button>
                                        ))}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        {/* View mode */}
                        <button
                            onClick={toggle}
                            className={`whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-all cursor-pointer ${isDark ? 'bg-white/5 border border-white/10 hover:bg-white/10' : 'bg-gray-100 border border-gray-200 hover:bg-gray-200'}`}
                            title={mode === 'simple' ? tt('nav.modeExpert', '전문가 모드') : tt('nav.modeSimple', '요약 모드')}
                        >
                            {mode === 'simple' ? (
                                <><BookOpen className="w-3.5 h-3.5 text-blue-500" /><span className={isDark ? 'text-gray-400' : 'text-gray-600'}>{tt('nav.modeSimple', '요약')}</span></>
                            ) : (
                                <><Code className="w-3.5 h-3.5 text-purple-500" /><span className={isDark ? 'text-gray-400' : 'text-gray-600'}>{tt('nav.modeExpert', '상세')}</span></>
                            )}
                        </button>
                        {/* CTA */}
                        <div className="flex items-center gap-2">
                           <button
                               onClick={openPopupRAGChat}
                               className="whitespace-nowrap px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 rounded-lg text-sm font-bold transition-all text-white cursor-pointer shadow-lg shadow-cyan-500/20"
                           >
                               SnapAgent
                           </button>
                           <button
                               onClick={openConsultationBot}
                               className="whitespace-nowrap px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 rounded-lg text-sm font-semibold transition-all text-white cursor-pointer"
                           >
                               {tt('nav.freeConsult', '무료 상담')}
                           </button>
                        </div>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${isDark
                                ? 'bg-white/5 text-white hover:bg-white/10'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        aria-label="메뉴 토글"
                    >
                        {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </nav>

            {/* Mobile Menu */}
            <AnimatePresence>
                {isOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
                            onClick={() => setIsOpen(false)}
                        />
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                            className={`fixed top-0 right-0 bottom-0 z-50 w-72 sm:w-80 overflow-y-auto shadow-2xl lg:hidden ${isDark ? 'bg-gray-950 border-l border-white/10' : 'bg-white border-l border-gray-200'}`}
                        >
                            {/* Header */}
                            <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                                <span className={`font-bold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>메뉴</span>
                                <button onClick={() => setIsOpen(false)} className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="p-3 space-y-1">
                                {/* Home */}
                                <Link href="/" onClick={() => setIsOpen(false)}
                                    className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium transition-all ${
                                        pathname === '/'
                                            ? isDark ? 'bg-blue-500/15 text-blue-400 border border-blue-500/20' : 'bg-blue-50 text-blue-700 border border-blue-200'
                                            : isDark ? 'text-gray-300 hover:bg-white/5' : 'text-gray-600 hover:bg-gray-50'
                                    }`}>
                                    <Home className="w-4 h-4" /> 홈
                                </Link>

                                {/* Mobile Accordion: Company */}
                                {([
                                    { id: 'company' as DropdownId, label: '회사소개', icon: Briefcase, items: COMPANY_LINKS.map(l => ({ ...l, type: 'hash' as const })) },
                                    { id: 'platform' as DropdownId, label: '플랫폼', icon: Layers, items: PLATFORM_LINKS.map(l => ({ ...l, type: 'link' as const, emoji: undefined })) },
                                ]).map(group => (
                                    <div key={group.id}>
                                        <button
                                            onClick={() => setMobileExpanded(mobileExpanded === group.id ? null : group.id)}
                                            className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl text-sm font-medium transition-all ${
                                                mobileExpanded === group.id
                                                    ? isDark ? 'bg-white/5 text-white' : 'bg-gray-50 text-gray-900'
                                                    : isDark ? 'text-gray-300 hover:bg-white/5' : 'text-gray-600 hover:bg-gray-50'
                                            }`}
                                        >
                                            <span className="flex items-center gap-3"><group.icon className="w-4 h-4" /> {group.label}</span>
                                            <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpanded === group.id ? 'rotate-180' : ''}`} />
                                        </button>
                                        <AnimatePresence>
                                            {mobileExpanded === group.id && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}
                                                    className="overflow-hidden"
                                                >
                                                    <div className="pl-6 pr-2 pb-2 space-y-0.5">
                                                        {group.items.map((item: any) => {
                                                            const href = item.isHash ? (pathname === '/' ? item.href : `/${item.href}`) : item.href;
                                                            const El = item.isHash ? 'a' : Link;
                                                            return (
                                                                <El key={item.href} href={href} onClick={() => setIsOpen(false)}
                                                                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs transition-all ${isDark ? 'text-gray-400 hover:bg-white/5 hover:text-white' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'}`}>
                                                                    {item.color ? (
                                                                        <div className={`w-5 h-5 rounded bg-gradient-to-br ${item.color} flex items-center justify-center text-white shrink-0 [&>svg]:w-2.5 [&>svg]:h-2.5`}>
                                                                            {item.icon}
                                                                        </div>
                                                                    ) : item.icon}
                                                                    <span>{item.label}</span>
                                                                    {item.isLive && <span className="text-red-500 text-[8px] animate-pulse">●</span>}
                                                                </El>
                                                            );
                                                        })}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ))}

                                {/* Family Sites */}
                                <div className={`mt-3 pt-3 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                                    <p className={`px-4 text-[10px] font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Family Sites</p>
                                    {FAMILY_SITES.map(site => (
                                        <a key={site.href} href={site.href} target="_blank" rel="noopener noreferrer" onClick={() => setIsOpen(false)}
                                            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all ${isDark ? 'text-gray-300 hover:bg-white/5' : 'text-gray-600 hover:bg-gray-50'}`}>
                                            <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${site.color} flex items-center justify-center text-white shrink-0`}>
                                                {site.icon}
                                            </div>
                                            <div>
                                                <div className="font-medium text-xs flex items-center gap-1">{site.label} <ExternalLink className="w-2.5 h-2.5 opacity-40" /></div>
                                                <div className={`text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{site.desc}</div>
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            </div>

                            {/* Footer */}
                            <div className={`p-4 mt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                                <div className="flex items-center gap-2 mb-3">
                                    <button onClick={toggleTheme}
                                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer ${isDark ? 'bg-white/5 border border-white/10 text-yellow-400 hover:bg-white/10' : 'bg-gray-100 border border-gray-200 text-blue-600 hover:bg-gray-200'}`}>
                                        {isDark ? <><Sun className="w-4 h-4" /> 라이트</> : <><Moon className="w-4 h-4" /> 다크</>}
                                    </button>
                                    <button onClick={toggle}
                                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer ${isDark ? 'bg-white/5 border border-white/10 text-gray-400 hover:bg-white/10' : 'bg-gray-100 border border-gray-200 text-gray-600 hover:bg-gray-200'}`}>
                                        {mode === 'simple' ? <><BookOpen className="w-4 h-4 text-blue-500" /> 요약</> : <><Code className="w-4 h-4 text-purple-500" /> 상세</>}
                                    </button>
                                </div>
                                <div className="flex gap-2">
                                  <button onClick={() => { setIsOpen(false); openPopupRAGChat(); }}
                                      className="flex-1 py-3 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 text-white text-sm font-bold cursor-pointer hover:from-cyan-700 hover:to-blue-700 shadow-lg shadow-cyan-500/20">
                                      SnapAgent
                                  </button>
                                  <button onClick={() => { setIsOpen(false); openConsultationBot(); }}
                                      className="flex-1 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-semibold cursor-pointer hover:from-blue-700 hover:to-indigo-700">
                                      {tt('nav.freeConsult', '무료 상담')}
                                  </button>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}
